import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { IconChevron } from './sidebarIcons'
import { NAV_TOP, NAV_GROUPS, NAV_SECONDARY, visibleChildren, leafVisible } from './navConfig'

const STORAGE_KEY = 'crm_nav_expanded_v1'

function isPathActive(to: string, pathname: string, end?: boolean): boolean {
  return pathname === to || (!end && pathname.startsWith(to + '/'))
}

// Левое меню (двухуровневое). open — развёрнутый сайдбар (показываем текст и
// раскрытие групп); свёрнутый — только иконки групп. Ширину сайдбара
// переключает кнопка в шапке (Layout); клик по иконке группы в свёрнутом меню
// разворачивает меню и открывает эту группу (onExpand).
export function SidebarNav({
  open,
  role,
  onNavClick,
  onExpand,
}: {
  open: boolean
  role: string
  onNavClick?: () => void
  onExpand?: () => void
}) {
  const { pathname } = useLocation()
  const groups = NAV_GROUPS.filter((g) => visibleChildren(g, role).length > 0)
  const secondary = NAV_SECONDARY.filter((l) => leafVisible(l, role))

  // Группа, содержащая активный маршрут — по умолчанию раскрыта.
  const activeGroupId = groups.find((g) =>
    visibleChildren(g, role).some((c) => isPathActive(c.to, pathname, c.end)),
  )?.id

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    } catch {
      return {}
    }
  })

  const isGroupOpen = (id: string) => (id in expanded ? expanded[id]! : id === activeGroupId)

  const toggle = (id: string, forceOpen = false) =>
    setExpanded((prev) => {
      const cur = id in prev ? prev[id]! : id === activeGroupId
      const next = { ...prev, [id]: forceOpen || !cur }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        /* localStorage недоступен — не критично */
      }
      return next
    })

  const leafCls = (active: boolean) =>
    `flex items-center py-2 rounded-lg transition-colors ${open ? 'gap-3 px-2.5' : 'justify-center'} ${
      active
        ? 'bg-brand-50 text-brand-700 font-medium'
        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
    }`

  return (
    <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
      {/* Dashboard — одиночка */}
      <NavLink
        to={NAV_TOP.to}
        data-testid={`nav-${NAV_TOP.to.replace(/^\//, '')}`}
        title={!open ? NAV_TOP.label : undefined}
        onClick={onNavClick}
        className={({ isActive }) => leafCls(isActive)}
      >
        {NAV_TOP.icon}
        {open && <span className="text-sm whitespace-nowrap">{NAV_TOP.label}</span>}
      </NavLink>

      {/* Группы */}
      {groups.map((g) => {
        const groupActive = g.id === activeGroupId
        const groupOpen = isGroupOpen(g.id)
        return (
          <div key={g.id}>
            <button
              onClick={() => {
                if (!open && onExpand) {
                  onExpand()
                  toggle(g.id, true)
                } else toggle(g.id)
              }}
              data-testid={`nav-group-${g.id}`}
              title={!open ? g.label : undefined}
              className={`w-full flex items-center py-2 rounded-lg transition-colors ${
                open ? 'gap-3 px-2.5' : 'justify-center'
              } ${
                groupActive
                  ? 'text-brand-700 font-medium'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              {g.icon}
              {open && (
                <>
                  <span className="flex-1 text-left text-sm whitespace-nowrap">{g.label}</span>
                  <IconChevron open={groupOpen} />
                </>
              )}
            </button>

            {open && groupOpen && (
              <div className="mt-0.5 space-y-0.5">
                {visibleChildren(g, role).map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    end={c.end}
                    data-testid={`nav-${c.to.replace(/^\//, '').replace(/\//g, '-')}`}
                    onClick={onNavClick}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 py-1.5 pl-8 pr-2.5 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-brand-50 text-brand-700 font-medium'
                          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                      }`
                    }
                  >
                    {c.icon}
                    <span className="whitespace-nowrap">{c.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )
      })}

      {/* Утилитарная зона — отдельными пунктами внизу */}
      {secondary.length > 0 && (
        <div className="pt-1 mt-1 border-t border-gray-100 space-y-0.5">
          {secondary.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.to.replace(/^\//, '').replace(/\//g, '-')}`}
              title={!open ? l.label : undefined}
              onClick={onNavClick}
              className={({ isActive }) => leafCls(isActive)}
            >
              {l.icon}
              {open && <span className="text-sm whitespace-nowrap">{l.label}</span>}
            </NavLink>
          ))}
        </div>
      )}
    </nav>
  )
}
